<template>
  <div class="gs-app gm-leads-refresh">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div class="w-full relative">
      <gd-banner
        v-if="showLimitBanner && contactLimitExceeded"
        :bannerMsg="bannerConfig.message"
        :bannerTextColor="bannerConfig.textColor"
        :bannerSize="bannerConfig.size"
        :bannerColor="bannerConfig.color"
        :bannerActions="bannerConfig.bannerActions"
      >
        <template slot="action-butons">
          <div class="mr-4">
            <gs-button
              :variant="bannerConfig.actionItems.actionBtnType === 'primary' ? 'primary' : 'outline'"
              size="sm"
              @click="goToUpgradePage()"
              class="ml-2"
              >{{ $t(bannerConfig.actionItems.actionBtnText) }}</gs-button
            >
          </div>
          <div class="cursor-pointer" @click="updateLimitBanner(false)">
            <span
              class="fas fa-times text-lg"
              :class="[bannerConfig.textColor]"
            ></span>
          </div>
        </template>
      </gd-banner>
    </div>
    <div class="flex mb-4 pb-10">
      <div class="w-full">
        <div id="mail-lead-view" class="mt-4 relative gm-leads-refresh__workspace">
          <div>
            <div class="flex w-full pt-3">
              <navigation-tabs :nav_tabs="navTabs"></navigation-tabs>
            </div>

            <ag-grid-table
              :class="{
                'hidden': !hasData && !isEmptyCardShow || !leadsFetched && !hasData
              }"

              ref="agGridTable"
              :dom-layout="domLayout"
              :grid-options="gridOptions"
              :columnDefs="leadsTableColumnDefs"
              :pagination="true"
              :rowHeight="leadsTableRowHeight"
              :rowBuffer="rowBuffer"
              :row-model-type="rowModelType"
              :datasource="dataSource"
              :pagination-page-size="paginationPageSize"
              :cache-overflow-size="cacheOverflowSize"
              :infiniteInitialRowCount="infiniteInitialRowCount"
              :maxBlocksInCache="maxBlocksInCache"
              :suppressRowClickSelection="true"
              :suppressCellSelection="true"
              :headerHeight="headerHeight"
              :context="context"
              :overlayNoRowsTemplate="overlayNoRowsTemplate"
              :frameworkComponents="frameworkComponents"
              :maxConcurrentDatasource-requests="maxConcurrentDatasourceRequests"
              :selectedRowSize="selectedRowSize"
              :rowSizeFilterData="rowSizeFilterData"
              :totalPages="totalPages"
              :totalRows="totalRows"
              :multiSelection="demoMode ? false : true"
              multiSelectUniqueId="lead_id"
              tableTitle='frontend_groovemail_components_pricepoint_sidebar_leads'
              :tableSubTitle='filterName'
              @changedSearchOrFilter="changedSearchOrFilter"
              @onPageSizeChanged="onPageSizeChanged"
              bulkActionName="frontend_affiliates_bulk_actions"
              :bulkOptions="bulkActions"
              :entityName="entityName"
              @getSelectionData="selectedData"
              @cellClicked="onCellClicked"
              @gridReady="onGridReady"

              @loading="showLoader"
              @loaded="hideLoader"

            >
              <template slot="top-right-content">
                
                <gs-button
                  variant="outline"
                  class="ml-4"
                  @click="openAdvancedFilters()"
                >
                  {{ 
                    this.isFilterApplied && !this.isFilterEmpty
                      ? $t('frontend_groovemail_lead_listing_edit_filters')
                      : $t('frontend_groovemail_lead_listing_filters')
                  }}
                  <FilterIcon class="h-4 w-4 ml-2"/>
                </gs-button>

                <gd-dropdown
                  v-if="userCanImportLeads == 1"
                  size="auto"
                  class="ml-4 dropdown-opacity-100"
                  :placeholder="this.$t('frontend_groovemail_mail_lead_index_new_lead')"
                  :options="dropdownOptions"
                  @change="handleOptionChange"
                />

                <gs-button
                  v-else
                  variant="primary"
                  class="ml-4"
                  @click="openCreateMaillead()"
                >
                  {{ $t('frontend_groovemail_mail_lead_index_new_lead') }}
                </gs-button>
              </template>
            </ag-grid-table>
            
            <div class="flex mt-5 justify-center"
              v-if="!hasData && !isEmptyCardShow && leadsFetched"
            >
              <empty-state
                :empty_state_heading="emptyStateData?.emptyStateHeading"
                :empty_state_sub_heading="emptyStateData?.emptyStateSubHeading"
                :empty_state_button_text="emptyStateData?.emptyStateBtnText"
                @executeDataCreation="openCreateMaillead"
                @showLoader="showLoader"
                @hideLoader="hideLoader"
              />
            </div>
          </div>
        </div>

        <!-- Create Maillead Modal -->
        <portal to="modalsTarget">
          <gs-named-modal
            height="85vh"
            ref="create-maillead-modal"
            name="create-maillead-modal"
            :title="$t('frontend_groovemail_lead_listing_create_new_lead')"
            
          >
            <div class="gs-app overflow-y-scroll custom-scrollbars" style="max-height: 95%;">
              <div class="p-6">
                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_firstname') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.first_name"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_firstname')"
                    :validator="$v.model.first_name"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_lastname') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.last_name"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_lastname')"
                    :validator="$v.model.last_name"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_email') }}
                  </label>
                  <text-input
                    variant="base"
                    type="email"
                    v-model="model.email"
                    :validator="$v.model.email"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_email')"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('frontend_groovemail_mail_lead_index_phone') }}</label>
                  <VuePhoneNumberInput
                    :border-radius="6"
                    :fetch-country="true"
                    :show-code-on-list="true"
                    v-model="model.phone_number"
                    :class="{
                      'error border-danger': this.$v.model.phone_number.$error
                    }"
                    @update="getPhoneData($event)"
                  />
                  <p
                    class="w-full error text-danger text-sm"
                    v-if="submitted && !this.phoneData.isValid && this.model.phone_number"
                  >
                    {{ $t('frontend_groovemail_settings_integeration_invalid_phone_number') }}
                  </p>

                  <p
                    class="w-full error text-danger text-sm"
                    v-if="this.$v.model.phone_number.$error"
                  > {{ $t('error_feild_required') }} </p>
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_company') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.company"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_company')"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_adress1') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.address_1"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_adress1')"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_adress2') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.address_2"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_adress2')"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_city') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.city"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_city')"
                  />
                </div>

                <div class="flex flex-col w-full mb-4">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_state') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.state"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_state')"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_country') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.country"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_country')"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_zipcode') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.zip"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_zipcode')"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_url') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.url"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_url')"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_taxnumber') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.tax_number"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_taxnumber')"
                  />
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_billing') }}
                  </label>
                  <text-input
                    variant="base"
                    v-model="model.billing_address"
                    :placeholder="$t('frontend_groovemail_mail_lead_index_billing')"
                  />
                </div>

                <div class="mb-6">
                  <div class="flex flex-col">
                    <label class="font-semibold text-sm text-grey-700 mb-2">
                      {{ $t('frontend_groovemail_mail_lead_index_assign_list') }}
                    </label>
                    <treeselect
                      class="custom-treeselect treeselect-add-new treeselect-select-shadow"
                      :class="{
                        'error border-danger': this.$v.model.leadlists.$error
                      }"
                      v-model="model.leadlists"
                      :multiple="true"
                      :max-height="230"
                      :options="listData"
                      :clearOnSelect="true"
                      :closeOnSelect="true"
                      :clearable="false"
                      @select="listSelected"
                      :placeholder="
                        $t('frontend_groovemail_lead_list_filter')
                      "
                    />
                  </div>
                  <p class="text-sm text-danger"
                    v-if="this.$v.model.leadlists.$error"
                  >
                    {{ $t('frontend_groovemail_forms_step4_select_atleast') }}
                  </p>
                </div>

                <div class="flex flex-col mb-6">
                  <label class="font-semibold text-sm text-grey-700 mb-2">
                    {{ $t('frontend_groovemail_mail_lead_index_assign_tag') }}
                  </label>
                  <treeselect
                    class="custom-treeselect treeselect-add-new treeselect-select-shadow"
                    v-model="model.leadtags"
                    :multiple="true"
                    :max-height="230"
                    :options="tagsData"
                    :clearOnSelect="true"
                    :closeOnSelect="true"
                    :clearable="false"
                    @select="tagSelected"
                    :placeholder="$t('frontend_groovemail_lead_tag_filter')"
                  />
                </div>

                <div class="flex justify-end mb-6">
                  <gs-button
                    variant="outline"
                    @click="closeCreateMaillead()"
                    >{{ $t('frontend_cancel') }}</gs-button
                  >
                  <gs-button
                    class="ml-2"
                    variant="primary"
                    @click="createMaillead()"
                  >
                    {{ $t('frontend_groovemail_mail_lead_index_create_lead') }}
                  </gs-button>
                </div>
              </div>
            </div>
          </gs-named-modal>
        </portal> 

        <!-- filter modal -->
        <gs-named-modal
          name="advanced-filters-modal"
          width="800px"
          :allowOverflow="false"
          id="advanced-lead-filter"
          :title="
            $t('frontend_groovemail_lead_listing_advanced_filters')
          "
        >
          <div class="gs-app p-6 gs-refresh-controls">
            <div>
              <div class="flex flex-col">
                <div class="text-sm font-semibold text-grey-700 mb-2">
                  {{ $t('frontend_groovemail_segment_subscribe_included') }}
                </div>
                <gd-filters
                  class="modal-filters"
                  ref="includeFilters"
                  :mainCondition="model.include_mainselect"
                  :binaryCondition="model.include_condition"
                  :binaryConditionData="binaryConditionData"
                  :mainConditionData="mainConditionData"
                  :addbtndisabled="inaddbtndisabled"
                  :firstConditionData="firstIncludeConditionData"
                  :filterFirstCondition="fetchFirstIncludeCondition"
                  :secondConditionData="secondIncludeConditionData"
                  :filterSecondCondition="fetchSecondIncludeCondition"
                  :selectedFilters="this.model.includesparams"
                  @mainConditionChanged="includemainselect"
                  @createNewFilter="includeaddbutton"
                  @updateMainCondition="model.include_mainselect = $event"
                  @updateBinaryCondition="model.include_condition = $event"
                  @updateSelectedFilters="model.includesparams = $event"
                  @firstConditionChanged="firstIncludedConditionUpdated"
                  @secondConditionChanged="secondIncludedConditionUpdated"
                  @deleteFilter="deleteinclude"
                ></gd-filters>
              </div>

              <div class="flex flex-col">
                <div class="text-sm font-semibold text-grey-700 mb-2">
                  {{ $t('frontend_groovemail_segment_subscribe_excluded') }}
                </div>
                <gd-filters
                  class="modal-filters"
                  ref="excludeFilters"
                  :mainCondition="model.exclude_mainselect"
                  :binaryCondition="model.exclude_condition"
                  :binaryConditionData="binaryConditionData"
                  :mainConditionData="mainConditionData"
                  :addbtndisabled="exaddbtndisabled"
                  :firstConditionData="firstExcludeConditionData"
                  :filterFirstCondition="fetchFirstExcludeCondition"
                  :secondConditionData="secondExcludeConditionData"
                  :filterSecondCondition="fetchSecondExcludeCondition"
                  :selectedFilters="this.model.excludesparams"
                  @mainConditionChanged="excludemainselect"
                  @createNewFilter="excludeaddbutton"
                  @updateMainCondition="model.exclude_mainselect = $event"
                  @updateBinaryCondition="model.exclude_condition = $event"
                  @updateSelectedFilters="model.excludesparams = $event"
                  @firstConditionChanged="firstExcludedConditionUpdated"
                  @secondConditionChanged="secondExcludedConditionUpdated"
                  @deleteFilter="deleteexclude"
                ></gd-filters>
              </div>
            </div>
            <div class="flex justify-end">
              <div>
                <gs-button
                  variant="outline"
                  class="ml-2"
                  @click="clearFilters()"
                >
                  {{ $t('frontend_groovemail_lead_listing_filters_reset') }}
                </gs-button>
              </div>
              <div class="ml-2">
                <gs-button
                  variant="primary"
                  @click="applyAdvancedFilters()"
                  :disabled="
                    this.isFilterEmpty && !this.isFilterApplied
                  "
                  >{{ $t('frontend_groovemail_lead_listing_advanced_filters_apply') }}
                </gs-button>
              </div>
            </div>
          </div>
        </gs-named-modal>

        <gs-named-modal
          id="bulkAddListsModal"
          :ref="`bulk-add-lists-modal`"
          name="bulk-add-lists-modal"
          :title="$t('frontend_groovemail_bulk_add_lists')"
        >
          <VuePerfectScrollbar>
            <div class="gs-app p-6">
              <div class="flex flex-col mt-2">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{ $t('frontend_groovemail_suppressions_enter_emails_unsubscribers_select_lists') }}
                </label>
                <treeselect
                  class="custom-treeselect treeselect-select-shadow"
                  v-model="model.leadlists"
                  :multiple="true"
                  :max-height="130"
                  :options="listDataForBulk"
                  :clearOnSelect="true"
                  :closeOnSelect="true"
                  :clearable="false"
                  :placeholder="$t('frontend_groovemail_lead_list_filter')"
                />
                <div
                  class="text-primary text-sm flex items-center font-semibold cursor-pointer mt-3"
                  @click="openCreateListModal"
                >
                  <PlusIcon class="h-5 w-5"/>
                  <p>{{ $t('frontend_groovemail_mail_list_newlist') }}</p>
                </div>
              </div>
              <div class="flex justify-end mt-13">
                <gs-button
                  variant="outline"
                  @click="closeBulkAddListsModal()"
                  >{{ $t('frontend_cancel') }}</gs-button
                >
                <gs-button
                  variant="primary"
                  class="ml-2"
                  :disabled="model.leadlists && model.leadlists.length == 0"
                  @click="confirmModalOpen('bulkAddLists')"
                >
                  {{ $t('frontend_save') }}</gs-button
                >
              </div>
            </div>
          </VuePerfectScrollbar>
        </gs-named-modal>

        <gs-named-modal
          id="bulkAddTagsModal"
          name="bulk-add-tags-modal"
          :title="$t('frontend_groovemail_bulk_add_tags')"
        >
          <VuePerfectScrollbar>
            <div class="gs-app p-6">
              <div class="flex flex-col mb-2">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{ $t('frontend_groovemail_bulk_add_tags_select_tags') }} 
                </label>
                <treeselect
                  class="custom-treeselect treeselect-select-shadow"
                  v-model="model.leadtags"
                  :multiple="true"
                  :max-height="130"
                  :options="tagsDataForBulk"
                  :clearOnSelect="true"
                  :closeOnSelect="true"
                  :clearable="false"
                  :placeholder="$t('frontend_groovemail_lead_tag_filter')"
                />
              </div>
              <div
                  class="text-primary text-sm flex items-center font-semibold cursor-pointer mt-3"
                  @click="openCreateTagModal"
                >
                  <PlusIcon class="h-5 w-5"/>
                  <p>{{ $t('frontend_groovemail_mail_tag_new_tag') }}</p>
              </div>
              <div class="flex justify-end mt-13">
                <gs-button
                  variant="outline"
                  @click="closeBulkAddTagsModal()"
                  >{{ $t('frontend_cancel') }}</gs-button
                >
                <gs-button
                  variant="primary"
                  class="ml-2"
                  :disabled="model.leadtags && model.leadtags.length == 0"
                  @click="confirmModalOpen('bulkAddTags')"
                >
                  {{ $t('frontend_save') }}</gs-button
                >
              </div>
            </div>
          </VuePerfectScrollbar>
        </gs-named-modal>

        <gs-named-modal
          name="create-maillist-in-leads-modal"
          :title="
            $t('frontend_groovemail_contacts_step4_create_new_list')
          "
        >
          <div class="gs-app">
            <create-list
              :type="'add'"
              :mail_list_id="null"
              :mail_list_data="mail_list_data"
              @showLoader="showLoader"
              @hideLoader="hideLoader"
              @fetchEventsList="fetchLists"
              @closeCreateMaillistModal="closeCreateMaillist"
            >
            </create-list>
          </div>
        </gs-named-modal>

        <gs-named-modal
          name="create-mailtag-in-leads-modal"
          :title="$t('frontend_groovemail_contacts_step4_create_newtag')"
        >
          <div class="gs-app">
            <create-tag
              :type="'add'"
              :mail_tag_id="null"
              :mail_tag_data="mail_tag_data"
              @showLoader="showLoader"
              @hideLoader="hideLoader"
              @fetchTags="fetchEventsTags"
              @closeCreateTagsModal="closeCreateMailtag"
            ></create-tag>
          </div>
        </gs-named-modal>

        <!-- Popup for free user after limit exceed Start -->
        <gs-named-modal
          ref="upgradeModal"
          name="upgrade-modal-for-limit"
          :title="$t('frontend_groovemail_upgrade_modal_heading')"
        >
          <div class="gs-app">
            <upgrade-content
              @closeUpgradeModal="closeUpgradeModalForFreeUsers"
            ></upgrade-content>
          </div>
        </gs-named-modal>

        <confirm-modal
          :ref="`confirmation-add`"
          :name="`confirmation-add`"
          :buttonText="confirmModalButtonText"
          :cancelText="$t('frontend_cancel')"
          :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
          :messageContent="confirmModalMessage"
          @confirm="confirmOrder"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import auth from '@groovepages/gd-lib/auth';
import axios from 'axios';
import {
  required,
  minLength,
  maxLength,
  email,
} from 'vuelidate/lib/validators';
import Treeselect from '@riophae/vue-treeselect';
import AgGridTable from '@groovepages/gd-ui-kit/components/tables/AgGridTable.vue';
import EmptyState from '@groovepages/gd-ui-kit/components/tables/EmptyState.vue';
import VuePhoneNumberInput from 'vue-phone-number-input';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import { mapGetters, mapState } from 'vuex';
import GDBanner from '@groovepages/gd-ui-kit/components/GDBanner';
import Mailtag from '@/models/Mailtag';
import Mailform from '@/models/Mailform';
import Mailsequence from '@/models/Mailsequence';
import Category from '@/models/Category';
import Maillead from '@/models/Maillead';
import Maillead2 from '@/models/Maillead2';

import themeConfig from '@/theme/themeConfig.js';

import ActionRenderer from '@/components/actionRenderer.js';
import TagNamesRenderer from '@/components/tagNamesRenderer.js';
import ListNamesRenderer from '@/components/listNamesRenderer.js';
import Filters from '@/components/filters.vue';
import showAlert from '@/components/sweet-alert-popup.js';

import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';

import NavigationTabs from '@/components/navigation-tabs.vue';
import CreateList from '@/components/create-lists.vue';
import CreateTag from '@/components/create-tags.vue';
import UpgradeContent from '@/components/UpgradeContent.vue';
import contactAddLimitExceeded from '@/components/user-plan-limit/contactAddLimit.js';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';


// import icons
import PlusIcon from '@/assets/images/icons/new/plus-small_new.svg';
import FilterIcon from '@/assets/images/icons/new/filter_new.svg';

export default {
  middleware: 'auth',
  layout: 'default',

  metaInfo() {
    return {
      title: this.$t('frontend_groovemail_components_pricepoint_sidebar_leads'),
    };
  },

  mixins: [demoModeValuesMixin],

  data() {
    return {
      filterName: '',
      isFilterApplied: false,
      isFilterEmpty: true,
      isFiltersAppliedFromQuery: false,
      mail_list_data: { name: '', description: '' },
      mail_tag_data: {
        name: '',
        description: '',
        category: [],
        categoryname: '',
      },
      entityName: {
        entityNameSingular: 'frontend_groovemail_contact_entity_name_singular',
        entityNamePlural: 'frontend_contacts'
      },
      totalPages: 0,
      totalRows: 0,
      statData: [],
      statModalTitle: '',
      submitted: false,
      phoneData: {
        countryCallingCode: '44',
        countryCode: 'GB',
        isValid: true,
      },
      categories: [],
      inaddbtndisabled: true,
      exaddbtndisabled: true,
      includesname: [],
      excludesname: [],
      includelistname: '',
      includetagname: '',
      excludelistname: '',
      excludetagname: '',
      includeformname: '',
      includesequencename: '',
      includeipname: '',
      includestatusname: '',
      includedaterangename: '',
      includecampaignname: '',
      includecampaignlinkname: '',
      includesegmentname: '',
      excludeformname: '',
      excludesegmentname: '',
      excludesequencename: '',
      excludeipname: '',
      excludestatusname: '',
      excludedaterangename: '',
      excludecampaignname: '',
      excludecampaignlinkname: '',
      includemainselectname: '',
      excludemainselectname: '',
      opens: 'left',
      autoApply: true,
      searchPhrase: '',
      selectedRowSize: 10 ,
      gridOptions: null,
      isAllSelected: false,
      unselectedLeadIds: [],
      selectedLeadIds: [],
      gridApi: null,
      leadsTableColumnDefs: null,
      paginationPageSize: 25,
      leadsTableRowHeight: 80,
      rowBuffer: null,
      rowModelType: null,
      cacheOverflowSize: null,
      infiniteInitialRowCount: null,
      maxBlocksInCache: null,
      frameworkComponents: null,
      maxConcurrentDatasourceRequests: null,
      dataSource: null,
      domLayout: 'autoHeight',
      leadsFetched: false,
      isEmailInvalid: false,
      leads: [],
      tags: [],
      includefiltertags: [],
      excludefiltertags: [],
      lists: [],
      forms: [],
      segments: [],
      sequences: [],
      campaigns: [],
      includelinks: [],
      excludelinks: [],
      windowWidth: window.innerWidth,
      singleColWidth: window.innerWidth / 15,
      model: {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        company: '',
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        country: '',
        zip: '',
        url: '',
        tax_number: '',
        billing_address: '',
        ip_address: '',
        leadtags: [],
        leadlists: [],
        include_mainselect: '',
        exclude_mainselect: '',
        include_condition: 'AND',
        exclude_condition: 'AND',
        includesparams: [],
        excludesparams: [],
        includelist: '',
        includetag: '',
        includeform: '',
        includesegment: '',
        includecategory: '',
        includesequence: '',
        includeip: '',
        includestatus: '',
        includedaterange: {
          startDate: moment('19700101', 'YYYYMMDD'),
          endDate: moment(),
        },
        includecampaign: '',
        includecampaignlink: '',
        excludelist: '',
        excludetag: '',
        excludeform: '',
        excludesegment: '',
        excludecategory: '',
        excludesequence: '',
        excludeip: '',
        excludestatus: '',
        excludedaterange: {
          startDate: moment('19700101', 'YYYYMMDD'),
          endDate: moment(),
        },
        excludecampaign: '',
        excludecampaignlink: '',
      },
      filterChanged: false,
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      contactLimitExceeded: false,
      showLimitBanner: false,
      bannerConfig: {
        message: this.$t(
          'frontend_groovemail_contact_limit_exceeded_error_message'
        ),
        size: 'basic',
        color: 'bg-primary-dark',
        textColor: 'text-primary-invert',
        bannerActions: true,
        actionItems: {
          actionBtnType: 'secondary',
          actionBtnText: 'frontend_groovemail_upgrade_btn',
        },
      },
      selectedOption: 'single',
      dropdownOptions: [
        {
          text: this.$t('frontend_groovemail_mail_lead_new_single_entry'),
          value: 'single',
        },
        {
          text: this.$t('frontend_groovemail_mail_lead_new_multiple_enteries'),
          value: 'multiple',
        },
      ],
      confirmModalMessage: '',
      confirmModalButtonText: '',
      confirmTarget: '',
      hasData: true,
      isEmptyCardShow: false,
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_components_pricepoint_sidebar_leads',
        emptyStateSubHeading: 'frontend_groovemail_contact_have_no_data',
        emptyStateBtnText: 'frontend_groovemail_mail_lead_index_create_lead',
      },
    };
  },

  validations: {
    model: {
      first_name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(50),
      },
      last_name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(50),
      },
      email: {
        required,
        email,
        minLength: minLength(3),
        maxLength: maxLength(50),
        isValidEmail() {
          return !this.isEmailInvalid;
        },
      },

      phone_number: {
        required,
      },

      leadlists: {
        required,
      },
    },
  },

  components: {
    Treeselect,
    VuePhoneNumberInput,
    VuePerfectScrollbar,
    'gd-filters': Filters,
    NavigationTabs,
    CreateList,
    CreateTag,
    UpgradeContent,
    'gd-banner': GDBanner,
    AgGridTable,
    PlusIcon,
    FilterIcon,
    EmptyState
  },

  watch: {
    // 'model.email'() {
    //   this.model.email = this.model.email.toLowerCase();
    // },
    'model.leadlists': function removeCreateListOption() {
      if (this.model.leadlists && this.model.leadlists.length) {
        const listIndex = this.model.leadlists.findIndex((ele) => {
          return ele === 'createListOption1';
        });
        if (listIndex >= 0) {
          this.model.leadlists.splice(listIndex, 1);
        }
      }
    },

    // watch the total rows to show/hide empty state
    'totalRows': function checkData() {
      if (this.totalRows === 0) {
        this.hasData = false;
      } else {
        this.hasData = true;
      }
    },

    // watch the leadsFetched to show/hide loader
    'leadsFetched': function checkData() {
      if (!this.leadsFetched) {
        this.showLoader();
      }
    },

    'model.leadtags': function removeCreateTagOption() {
      if (this.model.leadtags && this.model.leadtags.length) {
        const tagIndex = this.model.leadtags.findIndex((ele) => {
          return ele === 'createTagOption1';
        });
        if (tagIndex >= 0) {
          this.model.leadtags.splice(tagIndex, 1);
        }
      }
    },

    // watch the query params change and clear filter if query is empty and filter was applied
    '$route.query': function updateGrid() {
      // check if the query is empty
      const isQueryEmpty = Object.keys(this.$route.query).length === 0;

      // check if filter was applied and query is empty
      if (this.isFilterApplied && isQueryEmpty && this.isFilterEmpty) {
        // clear the filter data
        this.clearFilters();
      }
    },

    // Used to refresh leads table when demo mode changes
    demoMode() {
      this.getLeadsTableColDefs(this);
    },


    // watch the filter change to know if filter is empty or not
    'model.includesparams': function filterChange() {
      if(this.model.includesparams.length === 0 && this.model.excludesparams.length === 0) {
        this.isFilterEmpty = true;
      } else {
        this.isFilterEmpty = false;
      }
    },
    'model.excludesparams': function filterChange() {
      if(this.model.excludesparams.length === 0 && this.model.includesparams.length === 0) {
        this.isFilterEmpty = true;
      } else {
        this.isFilterEmpty = false;
      }
    },
  },

  methods: {
    ...mapGetters('mailing', ['getCanImportLeads']),

    openUpgradeModalForFreeUsers() {
      this.$root.$emit('modal-open', 'upgrade-modal-for-limit');
    },

    closeUpgradeModalForFreeUsers() {
      this.$root.$emit('modal-close', 'upgrade-modal-for-limit');
    },

    openAddMailleads() {
      if (!this.canImportLeads) {
        this.openUpgradeModalForFreeUsers();
      } else {
        this.$router
          .push({
            name: 'ImportLeads',
          })
          .catch(() => {});
      }
    },

    listSelected(node) {
      if (node.id === 'createListOption1') {
        this.openCreateListModal();
      }
    },

    // dropdown option change event handler 
    handleOptionChange(value) {
      if (value === 'single') {
        this.openCreateMaillead();
      } else if (value === 'multiple') {
        this.openAddMailleads();
      }
    },

    openCreateListModal() {
      this.mail_list_data.name = '';
      this.mail_list_data.description = '';
      this.$root.$emit('modal-open', 'create-maillist-in-leads-modal');
    },

    closeCreateMaillist() {
      this.$root.$emit('modal-close', 'create-maillist-in-leads-modal');
    },

    async fetchLists(listData, getListData = true) {
      if (getListData) {
        const mailLists = await axios.get(`/maillistonly`);
        this.lists = mailLists?.data?.data;
        if (listData && listData.data && listData.data.id) {
          this.model.leadlists.push(listData.data.id);
        }
      }
    },

    tagSelected(node) {
      if (node.id === 'createTagOption1') {
        this.openCreateTagModal();
      }
    },

    openCreateTagModal() {
      this.mail_tag_data.name = '';
      this.mail_tag_data.description = '';
      this.mail_tag_data.category = [];
      this.mail_tag_data.categoryname = '';
      this.$root.$emit('modal-open', 'create-mailtag-in-leads-modal');
    },

    async fetchEventsTags(tagData, getTagsData = true) {
      if (getTagsData) {
        this.tags = await Mailtag.params({lightweight:true}).$get();
        if (tagData && tagData.data && tagData.data.id) {
          this.model.leadtags.push(tagData.data.id);
        }
      }
    },

    closeCreateMailtag() {
      this.$root.$emit('modal-close', 'create-mailtag-in-leads-modal');
    },

    async fetchDataSources() {
      // show loader
      this.showLoader();

      try {
        if (!this.lists.length) {
          axios.get(`/maillistonly`).then((listData) => {
            this.lists = listData?.data?.data;
          });
        }

        if (!this.tags.length) {
          Mailtag.params({lightweight:true}).$get().then((res) => {
            this.tags = res;
          });
        }

        if (!this.categories.length) {
          Category.$get().then((res) => {
            this.categories = res;
          });
        }

        if (!this.forms.length) {
          Mailform.$get().then((res) => {
            this.forms = res;
          });
        }

        if (!this.segments.length) {
          axios.get('/listing/mailsegment').then((res) => {
            this.segments = res && res.data && res.data.length ? res.data : [];
          });
        }

        if (!this.sequences.length) {
          Mailsequence.$get().then((res) => {
            this.sequences = res;
          });
        }
        await this.fetchBroadcastsData();

        this.hideLoader();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async fetchBroadcastsData() {
      this.showLoader();
      try {
        const broadcastsData = await axios.get(`broadcast/list`);
        if (
          broadcastsData &&
          broadcastsData.data &&
          broadcastsData.data.data &&
          broadcastsData.data.data.length
        ) {
          this.campaigns = broadcastsData.data.data;
        } else {
          this.campaigns = [];
        }
        this.hideLoader();
      } catch (error) {
        this.campaigns = []; 
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async openBulkAddListsModal() {
      this.showLoader();

      try {
        await this.fetchLists(null, !(this.lists && this.lists.length));
        this.$root.$emit('modal-open', 'bulk-add-lists-modal');
        this.bulkActionType = '';
        
        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async openBulkAddTagsModal() {
      this.showLoader();

      try {
        await this.fetchEventsTags(null, !(this.tags && this.tags.length));
        this.$root.$emit('modal-open', 'bulk-add-tags-modal');
        this.bulkActionType = '';

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    closeBulkAddListsModal() {
      this.model.leadlists = [];
      this.$root.$emit('modal-close', 'bulk-add-lists-modal');
    },

    closeBulkAddTagsModal() {
      this.model.leadtags = [];
      this.$root.$emit('modal-close', 'bulk-add-tags-modal');
    },

    // open delete category modal
    async confirmModalOpen(target, id = null) {
      this.bulkActionType = target;

      // set the confirm modal message and button text based on the target type
      if (target === 'bulkAddLists') {
        this.confirmModalMessage = this.$t('frontend_groovemail_bulk_add_lists_are_you_sure_add_lists');
        this.confirmModalButtonText = this.$t('frontend_confirm');
      } else if (target === 'bulkAddTags') {
        this.confirmModalMessage = this.$t('frontend_groovemail_bulk_add_tags_are_you_sure_add_tags');
        this.confirmModalButtonText = this.$t('frontend_confirm');
      } else if (target === 'bulkDelete'){
        this.confirmModalMessage = this.$t('frontend_groovemail_bulk_delete_enteries_are_you_sure_delete_contacts');
        this.confirmModalButtonText = this.$t('frontend_delete');
      } else if (target === 'delete') {
        this.confirmModalMessage = this.$t('frontend_groovemail_swal_revert_message');
        this.confirmModalButtonText = this.$t('frontend_delete');
        this.confirmTarget = id;
      }
      
      // open the modal
      this.$refs[`confirmation-add`].open();
    },

    // close delete category modal
    closeConfirmModal() {
      // set the bulk action type to empty
      this.bulkActionType = '';

      // set the confirmTarget to empty
      this.confirmTarget = '';

      // close the modal
      this.$refs[`confirmation-add`].isVisible = false;
    },

    // confirm modal order
    confirmOrder() {
      if (this.bulkActionType === 'bulkAddLists') {
        this.bulkAddLists();
      } else if (this.bulkActionType === 'bulkAddTags') {
        this.bulkAddTags();
      } else if (this.bulkActionType === 'bulkDelete') {
        this.bulkDeleteLeads();
      } else if (this.bulkActionType === 'delete') {
        this.deleteLead(this.confirmTarget);
      }
    },

    async deleteLead(id) {
      // show loader
      this.showLoader();

      try {
        await axios.delete(`maillead/${id}`);
        
        // check if it is the last page and the last row
        if(this.totalRows >= 1) {
          this.isEmptyCardShow = false;
        }
        
        this.fetchEventsList();

        // close confirmation modal
        this.closeConfirmModal();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async bulkAddTags() {
      // show loader
      this.showLoader();
      try {
        const payload = this.prepareQueryForBulkActions();
        payload.tagsIds = this.model.leadtags;
        await axios.post('/lead/add-tags', {
          ...payload,
        });
        this.fetchEventsList();

        // close confirmation modal
        this.closeConfirmModal();

        // close bulk add tags modal
        this.closeBulkAddTagsModal();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async bulkAddLists() {
      // show loader
      this.showLoader();
      try {
        const payload = this.prepareQueryForBulkActions();
        payload.listsIds = this.model.leadlists;
        await axios.post('/lead/add-lists', {
          ...payload,
        });
        
        this.fetchEventsList();

        // close confirmation modal
        this.closeConfirmModal();

        // close bulk add lists modal
        this.closeBulkAddListsModal();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async bulkDeleteLeads() {
      // show loader
      this.showLoader();

      
      try {
        const payload = this.prepareQueryForBulkActions();
        payload.delete_scope = 'contact';
        await axios.post('/lead/delete', {
          ...payload,
        });

        // check if it is the last page and the last row
        if(this.totalRows >= 1) {
          this.isEmptyCardShow = false;
        }

        this.fetchEventsList();

        // close confirmation modal
        this.closeConfirmModal();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    prepareQueryForBulkActions() {
      return {
        isAllSelected: this.isAllSelected ? 'yes' : 'no',
        selected: this.selectedLeadIds,
        unselected: this.unselectedLeadIds,
        searchText: this.searchPhrase,
        includeType: this.model.include_condition,
        excludeType: this.model.exclude_condition,
        includedSegments: this.model.includesparams,
        excludedSegments: this.model.excludesparams,
      };
    },

    async downloadLeadExportFile() {
      if (!this.canExportLeads) {
        this.openUpgradeModalForFreeUsers();
      } else {
        this.showLoader();

        try {
          const exportQuery = this.prepareQueryForDowload();
          const exportLinkData = await axios.get('/download-lead-csv', {
            params: exportQuery,
          });
          const fileURL = window.URL.createObjectURL(
            new Blob([exportLinkData.data])
          );
          const fileLink = document.createElement('a');
          fileLink.href = fileURL;
          fileLink.setAttribute('download', 'leads-list.csv');
          document.body.appendChild(fileLink);
          fileLink.click();

          this.hideLoader();
        } catch (error) {
          // show error message
          this.requestErrorAlert(error.response?.data?.error);
        }
      }
    },

    prepareQueryForDowload() {
      // Define an object to hold the query parameters
      const queryParams = {
        page: this.gridApi.paginationGetCurrentPage() + 1,
        limit: this.gridApi.paginationGetPageSize(),
        search: this.searchPhrase,
        includeType: this.model.include_condition,
        excludeType: this.model.exclude_condition,
        selected: this.selectedLeadIds,
        isAllSelected: this.isAllSelected ? 'yes' : 'no',
      };

      // Check if there are unselected leads and add them to the query parameters
      if (this.unselectedLeadIds && this.unselectedLeadIds.length) {
        queryParams.unselected = this.unselectedLeadIds;
      } else {
        queryParams.unselected = [''];
      }

      // Check if there are include parameters and add them to the query parameters
      if (this.model.includesparams && this.model.includesparams.length) {
        let index = 0;
        this.model.includesparams.forEach((element) => {
          if (element.action) {
            const actionKey = `includedSegments[${index}][action]`;
            queryParams[actionKey] = element.action;
          }
          if (element.type) {
            const typeKey = `includedSegments[${index}][type]`;
            queryParams[typeKey] = element.type;
          }
          if (element.operation) {
            const operationKey = `includedSegments[${index}][operation]`;
            queryParams[operationKey] = element.operation;
          }
          if (element.id || element.id === 0) {
            const idKey = `includedSegments[${index}][id]`;
            queryParams[idKey] = element.id;
          }
          if (element.name) {
            const nameKey = `includedSegments[${index}][name]`;
            queryParams[nameKey] = element.name;
          }
          index += 1;
        });
      }

      // Check if there are exclude parameters and add them to the query parameters
      if (this.model.excludesparams && this.model.excludesparams.length) {
        let index = 0;
        this.model.excludesparams.forEach((element) => {
          if (element.action) {
            const actionKey = `excludedSegments[${index}][action]`;
            queryParams[actionKey] = element.action;
          }
          if (element.type) {
            const typeKey = `excludedSegments[${index}][type]`;
            queryParams[typeKey] = element.type;
          }
          if (element.operation) {
            const operationKey = `excludedSegments[${index}][operation]`;
            queryParams[operationKey] = element.operation;
          }
          if (element.id) {
            const idKey = `excludedSegments[${index}][id]`;
            queryParams[idKey] = element.id;
          }
          if (element.name) {
            const nameKey = `excludedSegments[${index}][name]`;
            queryParams[nameKey] = element.name;
          }
          index += 1;
        });
      }

      // Return the query parameters object
      return queryParams;
    },

    getPhoneData(event) {
      if (event.countryCallingCode) {
        this.phoneData.countryCallingCode = event.countryCallingCode;
      }
      if (event.countryCode) {
        this.phoneData.countryCode = event.countryCode;
      }
      this.phoneData.isValid = event.isValid;
    },

    firstIncludedConditionUpdated(conditionData) {
      if (this.model.include_mainselect === 'subscribedlist') {
        this.model.includelist = conditionData.condData;
        this.includelist(conditionData.event);
      } else if (this.model.include_mainselect === 'hastag') {
        this.model.includecategory = conditionData.condData;
        this.includecategory(conditionData.event);
      } else if (this.model.include_mainselect === 'hasform') {
        this.model.includeform = conditionData.condData;
        this.includeform(conditionData.event);
      } else if (this.model.include_mainselect === 'subscribedsegment') {
        this.model.includesegment = conditionData.condData;
        this.includesegment(conditionData.event);
      } else if (this.model.include_mainselect === 'subscribedsequence') {
        this.model.includesequence = conditionData.condData;
        this.includesequence(conditionData.event);
      } else if (this.model.include_mainselect === 'hasip') {
        this.model.includeip = conditionData.condData;
        this.includeip(conditionData.event);
      } else if (this.model.include_mainselect === 'openbroadcast' || this.model.include_mainselect === 'clickbroadcast') {
        this.model.includecampaign = conditionData.condData;
        this.includecampaign(conditionData.event);
      } else if (this.model.include_mainselect === 'indaterange') {
        const daterange = `${moment(conditionData.condData.startDate).format(
          'MM/DD/YYYY'
        )}-${moment(conditionData.condData.endDate).format('MM/DD/YYYY')}`;
        this.model.includedaterange = daterange;
        this.includedaterange(daterange);
      } else if (this.model.include_mainselect === 'hasstatus') {
        this.model.includestatus = conditionData.condData;
        this.includestatus(conditionData.event);
      }
    },

    secondIncludedConditionUpdated(conditionData) {
      if (this.model.include_mainselect === 'hastag') {
        this.model.includetag = conditionData.condData;
        this.includetag(conditionData.event);
      } else if (this.model.include_mainselect === 'clickbroadcast') {
        this.model.includecampaignlink = conditionData.condData;
        this.includecampaignlink(conditionData.event);
      }
    },

    firstExcludedConditionUpdated(conditionData) {
      if (this.model.exclude_mainselect === 'subscribedlist') {
        this.model.excludelist = conditionData.condData;
        this.excludelist(conditionData.event);
      } else if (this.model.exclude_mainselect === 'hastag') {
        this.model.excludecategory = conditionData.condData;
        this.excludecategory(conditionData.event);
      } else if (this.model.exclude_mainselect === 'hasform') {
        this.model.excludeform = conditionData.condData;
        this.excludeform(conditionData.event);
      } else if (this.model.exclude_mainselect === 'subscribedsegment') {
        this.model.excludesegment = conditionData.condData;
        this.excludesegment(conditionData.event);
      } else if (this.model.exclude_mainselect === 'subscribedsequence') {
        this.model.excludesequence = conditionData.condData;
        this.excludesequence(conditionData.event);
      } else if (this.model.exclude_mainselect === 'hasip') {
        this.model.excludeip = conditionData.condData;
        this.excludeip(conditionData.event);
      } else if (this.model.exclude_mainselect === 'openbroadcast' || this.model.exclude_mainselect === 'clickbroadcast') {
        this.model.excludecampaign = conditionData.condData;
        this.excludecampaign(conditionData.event);
      } else if (this.model.exclude_mainselect === 'indaterange') {
        const daterange = `${moment(conditionData.condData.startDate).format(
          'MM/DD/YYYY'
        )}-${moment(conditionData.condData.endDate).format('MM/DD/YYYY')}`;
        this.model.excludedaterange = daterange;
        this.excludedaterange(daterange);
      } else if (this.model.exclude_mainselect === 'hasstatus') {
        this.model.excludestatus = conditionData.condData;
        this.excludestatus(conditionData.event);
      }
    },

    secondExcludedConditionUpdated(conditionData) {
      if (this.model.exclude_mainselect === 'hastag') {
        this.model.excludetag = conditionData.condData;
        this.excludetag(conditionData.event);
      } else if (this.model.exclude_mainselect === 'clickbroadcast') {
        this.model.excludecampaignlink = conditionData.condData;
        this.excludecampaignlink(conditionData.event);
      }
    },

    // store the filter data
    storeFilterData() {
      // apply filters data to store
      const filters = {
        include_mainselect: this.model.include_mainselect,
        exclude_mainselect: this.model.exclude_mainselect,
        include_condition: this.model.include_condition,
        exclude_condition: this.model.exclude_condition,
        includesparams: this.model.includesparams,
        excludesparams: this.model.excludesparams,
        includelist: this.model.includelist,
        includetag: this.model.includetag,
        includesegment: this.model.includesegment,
        includeform: this.model.includeform,
        includecategory: this.model.includecategory,
        includesequence: this.model.includesequence,
        includeip: this.model.includeip,
        includestatus: this.model.includestatus,
        includedaterange: this.model.includedaterange,
        includecampaign: this.model.includecampaign, 
        includecampaignlink: this.model.includecampaignlink, 
        excludelist: this.model.excludelist, 
        excludetag: this.model.excludetag, 
        excludesegment: this.model.excludesegment, 
        excludeform: this.model.excludeform, 
        excludecategory: this.model.excludecategory, 
        excludesequence: this.model.excludesequence, 
        excludeip: this.model.excludeip, 
        excludestatus: this.model.excludestatus, 
        excludedaterange: this.model.excludedaterange, 
        excludecampaign: this.model.excludecampaign, 
        excludecampaignlink: this.model.excludecampaignlink, 
      };

      // dispatch filters data to store
      this.$store.dispatch('setFilters', filters);
    },

    // clear the filter data from store
    clearFilterData() {
      // dispatch clear filters
      this.$store.dispatch('clearFilters');

      // clear the query filters
      this.$store.dispatch('clearFilterQuery');

      // reset the filter name
      this.filterName = '';
    },

    // set the filter changed flag
    setFilterChangedFlag(flag) {
      this.filterChanged = flag;
    },

    // apply filters
    applyAdvancedFilters() {
      // show loader
      this.showLoader();

      // reset the query filters and change the filter name if the filter is changed
      if(this.filterChanged) {
        this.$store.dispatch('clearFilterQuery');
        this.filterName = this.$t('frontend_groovemail_leads_custom_filters_applied');
      }

      // check if the filter is empty if yes then clear the filter data
      if(this.isFilterEmpty) {
        this.clearFilters();
        
        // stop the execution
        return;
      }
      
      // set the filter changed flag to true
      this.isEmptyCardShow = true;

      // was filter applied
      this.isFilterApplied = true;

      // store the filter data
      this.storeFilterData();

      this.changedSearchOrFilter(this.searchPhrase);
      this.reloadLeadsListing();
      this.isAllSelected = false;
      this.closeAdvancedFilters();
    },

    // reload the leads listing page with updated query params
    reloadLeadsListing() {
      // show loader
      this.showLoader();

      const catchErrorRoute = (error) => {
        // hide loader
        this.hideLoader();

        if (error.name !== 'NavigationDuplicated') {
          throw error;
        }
      };

      // reload the leads listing page
      if(this.filterChanged) {
        this.$router.replace({
          name: this.$route.name
        }).catch(error => {
          catchErrorRoute(error);
        });

      } else {
        this.$router.replace({
          name: this.$route.name,
          // keep the query params as it is
          query: {...this.$route.query,},
        })
        .catch(error => {
          catchErrorRoute(error);
        });
        
      }
      
      // set the filter changed flag to false
      this.setFilterChangedFlag(false);
      
      // hide loader
      this.hideLoader();
    },

    includeform(e) {
      this.includeformname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.inaddbtndisabled = this.model.includeform === '';
    },

    excludeform(e) {
      this.excludeformname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.exaddbtndisabled = this.model.excludeform === '';
    },

    includesegment(e) {
      this.includesegmentname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.inaddbtndisabled = this.model.includesegment === '';
    },

    excludesegment(e) {
      this.excludesegmentname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.exaddbtndisabled = this.model.excludesegment === '';
    },

    includelist(e) {
      this.includelistname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.inaddbtndisabled = this.model.includelist === '';
    },

    excludelist(e) {
      this.excludelistname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.exaddbtndisabled = this.model.excludelist === '';
    },

    includetag(e) {
      this.includetagname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.inaddbtndisabled = this.model.includetag === '';
    },

    excludetag(e) {
      this.excludetagname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.exaddbtndisabled = this.model.excludetag === '';
    },

    includemainselect(e) {
      this.includemainselectname = e.target.options[e.target.options.selectedIndex].dataset.name;
      if (this.model.include_mainselect) {
        this.inaddbtndisabled = true;
      }
      this.includefiltertags = '';
      this.includelinks = '';
    },

    excludemainselect(e) {
      this.excludemainselectname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      if (this.model.exclude_mainselect) {
        this.exaddbtndisabled = true;
      }
      this.excludefiltertags = '';
      this.excludelinks = '';
    },

    async includecategory(e) {
      // show loader
      this.showLoader();
      const self = this;
      self.includefiltertags = [];

      try {
        const { data } = await axios.get('/mailtag', {
          params: { category: e.target.value },
        });

        if (data && data.length > 0) {
          self.includefiltertags = data;
        }

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async excludecategory(e) {
      // show loader
      this.showLoader();

      const self = this;
      self.excludefiltertags = [];
      
      try {
        const { data } = await axios.get('/mailtag', {
          params: { category: e.target.value },
        });
        if (data && data.length > 0) {
          self.excludefiltertags = data;
        }

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    includesequence(e) {
      this.includesequencename =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.inaddbtndisabled = this.model.includesequence === '';
    },

    excludesequence(e) {
      this.excludesequencename =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.exaddbtndisabled = this.model.excludesequence === '';
    },

    includestatus(e) {
      this.includestatusname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.inaddbtndisabled = this.model.includestatus === '';
    },

    excludestatus(e) {
      this.excludestatusname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.exaddbtndisabled = this.model.excludestatus === '';
    },

    includeip(e) {
      this.includeipname = e;
      this.inaddbtndisabled = this.model.includeip === '';
    },

    excludeip(e) {
      this.excludeipname = e;
      this.exaddbtndisabled = this.model.excludeip === '';
    },

    includedaterange(e) {
      this.includedaterangename = e;
      this.inaddbtndisabled = this.model.includedaterange === '';
    },

    excludedaterange(e) {
      this.excludedaterangename = e;
      this.exaddbtndisabled = this.model.excludedaterange === '';
    },

    async includecampaign(e) {
      // show loader
      this.showLoader();

      this.includecampaignname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      if (this.model.include_mainselect === 'openbroadcast') {
        this.inaddbtndisabled = this.model.includecampaign === '';

        // hide loader
        this.hideLoader();
      } else if (this.model.include_mainselect === 'clickbroadcast') {
        try {
          const linksdata = await axios.post('/campaign-tracking-link', {
            campaign_id: e.target.options[e.target.options.selectedIndex].value,
          });
          this.includelinks = [];
          if (linksdata.data && linksdata.data.length) {
            linksdata.data.forEach((ele) => {
              const linkObj = {
                id: ele.id,
                name: ele.source_id,
                linkName: ele.redirect_url,
              };

              this.includelinks.push(linkObj);
            });
          }
          this.inaddbtndisabled = true;

          // hide loader
          this.hideLoader();
        } catch (error) {
          // show error message
          this.requestErrorAlert(error.response?.data?.error);
        }
      }
    },

    async excludecampaign(e) {
      this.excludecampaignname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      if (this.model.exclude_mainselect === 'openbroadcast') {
        this.exaddbtndisabled = this.model.excludecampaign === '';
      } else if (this.model.exclude_mainselect === 'clickbroadcast') {
        try {
          const linksdata = await axios.post('/campaign-tracking-link', {
            campaign_id: e.target.options[e.target.options.selectedIndex].value,
          });
          this.excludelinks = [];
          if (linksdata.data && linksdata.data.length) {
            linksdata.data.forEach((ele) => {
              const linkObj = {};
              linkObj.id = ele.id;
              linkObj.name = ele.source_id;
              linkObj.linkName = ele.redirect_url;
              this.excludelinks.push(linkObj);
            });
          }
          this.exaddbtndisabled = true;

          // hide loader
          this.hideLoader();
        } catch (error) {
          // show error message
          this.requestErrorAlert(error.response?.data?.error);
        }
      }
    },

    includecampaignlink(e) {
      this.includecampaignlinkname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.inaddbtndisabled = this.model.includecampaignlink === '';
    },

    excludecampaignlink(e) {
      this.excludecampaignlinkname =
        e.target.options[e.target.options.selectedIndex].dataset.name;
      this.exaddbtndisabled = this.model.excludecampaignlink === '';
    },

    checkFilterPresent(filtersArray, filterId) {
      let present = false;
      filtersArray.forEach((ele) => {
        if (ele.id === filterId) {
          present = true;
        }
      });
      return present;
    },

    showError() {
      // hide loader
      this.hideLoader();

      // show error message
      showAlert({
        iconType: 'alert-error',
        html: `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${this.$t('frontend_groovemail_filter_already_present_text')}</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    /**
     * Adds a filter to the model.includesparams array based on the selected filter type.
     * @param {string} type - The type of filter to add (e.g. 'list', 'tag', 'segment', etc.).
     * @param {string} id - The ID of the selected filter.
     * @param {string} name - The name of the selected filter.
     * @param {string} action - The main action associated with the selected filter.
     * @param {string} notAction - The opposite action associated with the selected filter.
     * @param {string} linkName - The name of the link associated with the selected filter (for 'clickbroadcast' filters only).
    */
    incloudFilter(type, id, name, action, notAction, condition, linkName = '') {
      // Check if the filter is already present in the model.includesparams array.
      const present = this.checkFilterPresent(this.model.includesparams, id);
      if (present) {
        // If the filter is already present, show an error message.
        this.showError();
      } else {
        // If the filter is not already present, add it to the model.includesparams array.
        const listtxt = `${this.includemainselectname} ${name}`;
        this.includesname.push(listtxt);

        const operation = condition === 'AND' ? '=' : '!=';

        this.model.includesparams.push({
          action,
          type,
          operation,
          id,
          name,
          linkName,
        });
        // if the filters is applied form query then no need for clear the filter
        if(!this.isFiltersAppliedFromQuery) {
          // Clear the include filter
          this.clearIncludeFilterParams();
        }
      }
    },
    
    
    // clear the include filter params and disable the add button
    clearIncludeFilterParams() {
      // reset the filters component
      this.$refs.includeFilters.resetFilter();

      this.model.include_mainselect = null;
      this.model.include_condition = 'AND';

      // disable the add button
      this.inaddbtndisabled = true;
    },

    /**
     * Adds a filter based on the selected filter type.
     */
    async includeaddbutton() {
      // set the filters applied form qery to false
      this.isFiltersAppliedFromQuery = false;

      switch (this.model.include_mainselect) {
        case 'subscribedlist':
          this.incloudFilter( 'list', this.model.includelist, this.includelistname, this.model.include_mainselect, 'notsubscribedlist', this.model.include_condition );
          break;
        case 'hastag':
          this.incloudFilter( 'tag', this.model.includetag, this.includetagname, this.model.include_mainselect, 'notag', this.model.include_condition );
          break;
        case 'subscribedsegment':
          this.incloudFilter( 'segment', this.model.includesegment, this.includesegmentname, this.model.include_mainselect, 'notsegment', this.model.include_condition );
          break;
        case 'hasform':
          this.incloudFilter( 'form', this.model.includeform, this.includeformname, this.model.include_mainselect, 'notform', this.model.include_condition );
          break;
        case 'subscribedsequence':
          this.incloudFilter( 'sequence', this.model.includesequence, this.includesequencename, this.model.include_mainselect, 'notsequence', this.model.include_condition );
          break;
        case 'hasip':
          this.incloudFilter( 'ip', this.model.includeip, this.includeipname, this.model.include_mainselect, 'notip', this.model.include_condition );
          break;
        case 'hasstatus':
          this.incloudFilter( 'status', this.model.includestatus, this.includestatusname, this.model.include_mainselect, 'notstatus', this.model.include_condition );
          break;
        case 'indaterange':
          this.incloudFilter( 'daterange', this.model.includedaterange, this.includedaterangename, this.model.include_mainselect, 'notdaterange', this.model.include_condition );
          break;
        case 'openbroadcast':
          this.incloudFilter( 'broadcastopen', this.model.includecampaign, this.includecampaignname, this.model.include_mainselect, 'notopenbroadcast', this.model.include_condition );
          break;
        case 'clickbroadcast': {
          const linkObj = this.includelinks.find((ele) => ele.id === this.includecampaignlinkname);
          this.incloudFilter('broadcastclick', linkObj.id, linkObj.name, this.model.include_mainselect, 'notcampaignlink', this.model.include_condition, linkObj.linkName);
          break;
        }
        default: {
          // If no filter is selected, add the main select name to the includesname array and disable the add button.
          this.includesname.push(this.includemainselectname);
          this.model.includesparams.push({
            action: this.model.include_mainselect,
            type: '',
            operation: '',
            id: '',
            name: '',
          });
          this.model.include_condition = 'AND';
          this.inaddbtndisabled = true;
        }
      }
      
      // set the filter chnage flag to true
      this.setFilterChangedFlag(true);
    },


    /**
     * Adds a filter to the exclude list if it is not already present.
     * @param {string} type - The type of filter to add (e.g. list, tag, form).
     * @param {string} id - The ID of the filter to add.
     * @param {string} name - The name of the filter to add.
     * @param {string} action - The main select action for the filter.
     * @param {string} notAction - The opposite action for the filter.
     * @param {string} linkName - The name of the link for broadcast click filters.
    */
    excludesFilter(type, id, name, action, notAction, condition, linkName = '') {
      // Check if the filter is already present in the model.includesparams array.
      const present = this.checkFilterPresent(this.model.excludesparams, id);
      if (present) {
        this.showError();
      } else {
        const listtxt = `${this.excludemainselectname} ${name}`;
        this.excludesname.push(listtxt);
        const operation = condition === 'AND' ? '=' : '!=';
        this.model.excludesparams.push({
          action,
          type,
          operation,
          id,
          name,
          linkName,
        });
      }

      // clear the exclude filter
      this.clearExcludeFilterParams();
    },

    // clear the exclude filter params and disable the add button
    clearExcludeFilterParams() {
      // reset the filters component
      this.$refs.excludeFilters.resetFilter();

      this.model.exclude_mainselect = null;
      this.model.exclude_condition = 'AND';

      // disable the add button
      this.exaddbtndisabled = true;
    },

    /**
     * Adds a filter to the exclude list based on the selected main select action.
     * Calls the excludesFilter function with the appropriate parameters for each action.
     */
    async excludeaddbutton() {
      switch (this.model.exclude_mainselect) {
        case 'subscribedlist':
          this.excludesFilter( 'list', this.model.excludelist, this.excludelistname, this.model.exclude_mainselect, 'notsubscribedlist', this.model.exclude_condition);
          break;
        case 'hastag':
          this.excludesFilter( 'tag', this.model.excludetag, this.excludetagname, this.model.exclude_mainselect, 'notag', this.model.exclude_condition);
          break;
        case 'hasform':
          this.excludesFilter( 'form', this.model.excludeform, this.excludeformname, this.model.exclude_mainselect, 'notform', this.model.exclude_condition);
          break;
        case 'subscribedsegment':
          this.excludesFilter( 'segment', this.model.excludesegment, this.excludesegmentname, this.model.exclude_mainselect, 'notsegment', this.model.exclude_condition);
          break;
        case 'subscribedsequence':
          this.excludesFilter( 'sequence', this.model.excludesequence, this.excludesequencename, this.model.exclude_mainselect, 'notsequence', this.model.exclude_condition);
          break;
        case 'hasip':
          this.excludesFilter( 'ip', this.model.excludeip, this.excludeipname, this.model.exclude_mainselect, 'notip', this.model.exclude_condition);
          break;
        case 'hasstatus':
          this.excludesFilter( 'status', this.model.excludestatus, this.excludestatusname, this.model.exclude_mainselect, 'notstatus', this.model.exclude_condition);
          break;
        case 'indaterange':
          this.excludesFilter( 'daterange', this.model.excludedaterange, this.excludedaterangename, this.model.exclude_mainselect, 'notdaterange', this.model.exclude_condition);
          break;
        case 'openbroadcast':
          this.excludesFilter( 'broadcastopen', this.model.excludecampaign, this.excludecampaignname, this.model.exclude_mainselect, 'notopenbroadcast', this.model.exclude_condition);
          break;
        case 'clickbroadcast': {
          const linkObj = this.excludelinks.find((ele) => ele.id === this.excludecampaignlinkname);
          this.excludesFilter( 'broadcastclick', linkObj.id, linkObj.name, this.model.exclude_mainselect, 'notcampaignlink', this.model.exclude_condition, linkObj.linkName);
          break;
        }
        default: {
          this.excludesname.push(this.excludemainselectname);
          this.model.excludesparams.push({
            action: this.model.exclude_mainselect,
            type: '',
            operation: '',
            id: '',
            name: '',
          });
          this.model.exclude_condition = 'AND';
          this.exaddbtndisabled = true;
        }
      }

      // set the filter chnage flag to true
      this.setFilterChangedFlag(true);
    },

    async deleteinclude(option) {
      const index = this.model.includesparams.indexOf(option);
      if (index > -1) {
        this.includesname.splice(index, 1);
        this.model.includesparams.splice(index, 1);
      }

      // set the filter chnage flag to true
      this.setFilterChangedFlag(true);
    },

    async deleteexclude(option) {
      const index = this.model.excludesparams.indexOf(option);
      if (index > -1) {
        this.excludesname.splice(index, 1);
        this.model.excludesparams.splice(index, 1);
      }

      // set the filter chnage flag to true
      this.setFilterChangedFlag(true);
    },

    async openAdvancedFilters() {
      this.showLoader();
      this.$root.$emit('modal-open', 'advanced-filters-modal');
      this.model.include_mainselect = '';
      this.model.exclude_mainselect = '';
      this.includefiltertags = '';
      this.includelinks = '';
      this.excludefiltertags = '';
      this.excludelinks = '';
      await this.fetchDataSources();
      this.hideLoader();
    },

    closeAdvancedFilters() {
      this.$root.$emit('modal-close', 'advanced-filters-modal');
    },

    clearFilters() {
      // show loader
      this.showLoader();

      this.inaddbtndisabled = true;
      this.exaddbtndisabled = true;
      this.includesname = [];
      this.excludesname = [];
      this.includelistname = '';
      this.includetagname = '';
      this.excludelistname = '';
      this.excludetagname = '';
      this.includesegmentname = '';
      this.includeformname = '';
      this.includesequencename = '';
      this.includeipname = '';
      this.includestatusname = '';
      this.includedaterangename = '';
      this.includecampaignname = '';
      this.includecampaignlinkname = '';
      this.excludesegmentname = '';
      this.excludeformname = '';
      this.excludesequencename = '';
      this.excludeipname = '';
      this.excludestatusname = '';
      this.excludedaterangename = '';
      this.excludecampaignname = '';
      this.excludecampaignlinkname = '';
      this.includemainselectname = '';
      this.excludemainselectname = '';
      this.model = {
        include_mainselect: '',
        exclude_mainselect: '',
        include_condition: 'AND',
        exclude_condition: 'AND',
        includesparams: [],
        excludesparams: [],
        includelist: '',
        includetag: '',
        includesegment: '',
        includeform: '',
        includecategory: '',
        includesequence: '',
        includeip: '',
        includestatus: '',
        includedaterange: '',
        includecampaign: '',
        includecampaignlink: '',
        excludelist: '',
        excludetag: '',
        excludesegment: '',
        excludeform: '',
        excludecategory: '',
        excludesequence: '',
        excludeip: '',
        excludestatus: '',
        excludedaterange: '',
        excludecampaign: '',
        excludecampaignlink: '',
      };

      // set the show empty card flag to false
      this.isEmptyCardShow = false;

      // was filter applied flag to false
      this.isFilterApplied = false;

      // clear the filter data from store
      this.clearFilterData();

      // set the filter chnage flag to true
      this.setFilterChangedFlag(true);

      this.isAllSelected = false;
      this.changedSearchOrFilter(this.searchPhrase);
      this.reloadLeadsListing();
      this.closeAdvancedFilters();

    },

    // go to the lead detail page on click of the name column
    async onCellClicked(e) {
      // store the filter data
      this.storeFilterData();

      // check if the clicked column is name column
      if (e.colDef.headerName === this.$t('frontend_groovemail_forms_index_name') || e.colDef.headerName === 'Name') {
        this.$router.push({
          name: 'LeadDetail',
          params: { id: e.data.lead_id },
        });
      }
    },

    selectedData(isAllSelected, selected, unselected) {
      this.isAllSelected = isAllSelected;
      this.selectedLeadIds = selected;
      this.unselectedLeadIds = unselected;
    },

    /* 
      * Fetches the leads data from the API and sets the data source for the leads grid.
    */
    async onGridReady() {
      // set the query parameters as filters
      await this.setQueryAsFilters();

      // get the grid data
      this.gridApi = this.gridOptions.api;
      this.gridOptions.rowHeight = 80;
      this.gridColumnApi = this.gridOptions.columnApi;
      this.selectedRowSize = 10;

      this.dataSource = await this.getDataSource(Number(this.selectedRowSize));

      // set the page size for the grid
      this.onPageSizeChanged(this.selectedRowSize);
    },

    /**
     * Sets the query parameters as filters.
    */
    async setQueryAsFilters() {
      // Check if the route has any query parameters and add them to the model.includesparams array.
      if (this.$route.query) {
        // set the filters applied from query flag to true
        this.isFiltersAppliedFromQuery = true;

        // set the show empty card flag to true
        this.isEmptyCardShow = true;

        // was filter applied flag to true
        this.isFilterApplied = true;

        let filterQuery = '';

        // check if the route has tags query parameter
        if (this.$route.query.tagId) {
          this.incloudFilter('tag', this.$route.query.tagId, this.$route.query.tagName, 'hastag', 'notag', 'AND');

          // set the filter name based on the query parameters
          filterQuery = `tag ( ${this.$route.query.tagName} )`;
        } 
        
        // check if the route has lists query parameter
        if (this.$route.query.listId) {
          this.incloudFilter('list', this.$route.query.listId, this.$route.query.listName, 'subscribedlist', 'notsubscribedlist', 'AND');

          // set the filter name based on the query parameters
          filterQuery = `list ( ${this.$route.query.listName} )`;
        } 
        
        // check if the route has segments query parameter
        if (this.$route.query.segmentId) {
          this.incloudFilter('segment', this.$route.query.segmentId, this.$route.query.segmentName, 'subscribedsegment', 'notsegment', 'AND');

          // set the filter name based on the query parameters
          filterQuery = `segment ( ${this.$route.query.segmentName} )`;
        } 
        
        // check if the route has sequences query parameter
        if (this.$route.query.sequenceId) {
          this.incloudFilter('sequence', this.$route.query.sequenceId, this.$route.query.sequenceName, 'subscribedsequence', 'notsubscribedsequence', 'AND');

          // set the filter name based on the query parameters
          filterQuery = `sequence ( ${this.$route.query.sequenceName} )`;
        } 

        // check if the route has unsubscribed lists query parameter
        if (this.$route.query.unsubscribedListId) {
          this.incloudFilter('list', this.$route.query.unsubscribedListId, this.$route.query.unsubscribedListName, 'notsubscribedlist', 'subscribedlist', 'AND');
        }
        
        // check if the route has status query parameter
        if (this.$route.query.statusId) {
          this.incloudFilter('status', this.$route.query.statusId, this.$route.query.statusName, 'hasstatus', 'notstatus', 'AND');
        }

        // check if the route has broadcsts open query parameter
        if(this.$route.query.broadcastsOpenId) {
          this.incloudFilter('broadcastopen', this.$route.query.broadcastsOpenId, this.$route.query.broadcastsOpenName, 'openbroadcast', 'notopenbroadcast', 'AND');

          // set the filter name based on the query parameters
          filterQuery = `broadcast ( ${this.$route.query.broadcastsOpenName} )`;
        }

        // check if the route has formsubmissions query parameter
        if(this.$route.query.formSubmissionsId) {
          this.model.includesparams.push({
            action: 'hasform',
            id: this.$route.query.formSubmissionsId,
            name: this.$route.query.formSubmissionsName,
            operation: '=',
            type: 'form',
          });
        }

        // check if the filter query is not empty and set the filter name
        this.filterName = filterQuery !== '' ? `${this.$t('frontend_groovemail_leads_filters_applied')} ${filterQuery}` : '';

        // save the filter data to store
        this.$store.dispatch('setFilterQuery', this.$route.query);
      }
    },

    onPageSizeChanged(event) {
      // show loader
      this.showLoader();

      this.selectedRowSize = event;
      this.gridApi.gridOptionsWrapper.setProperty(
        'cacheBlockSize',
        +this.selectedRowSize
      );

      // reset the cache
      this.gridApi.infiniteRowModel.resetCache();

      // set the page size
      this.gridApi.paginationSetPageSize(+this.selectedRowSize);
    },

    /**
     * Returns a new data source for the leads grid.
     * The data source fetches leads data from the API based on the current route.
     * @returns {Object} A new data source for the leads grid.
     */
    getDataSource() {
      const self = this;

      // Define the MyDatasource constructor.
      function MyDatasource(rowCount) {
        this.rowCount = rowCount;
      }

      // If leads data has not been fetched yet, define the getRows method.
      if (!this.leadsFetched) {
        MyDatasource.prototype.getRows = async function fetchLeadsData(params) {
          // show loader
          self.showLoader();  
  
          // Fetch leads data from the API based on the current route.
          let leadsData;
          const api = self.$route.name === 'Leads' ? Maillead : Maillead2;
          if (self.$route.name === 'Leads' || self.$route.name === 'Leads2') {
  
            try {
              self.leads = await api.params({
                page: self.gridApi.paginationGetCurrentPage() + 1,
                limit: self.gridApi.paginationGetPageSize(),
                search: self.searchPhrase,
                includedSegments: self.model.includesparams,
                excludedSegments: self.model.excludesparams,
                includeType: self.model.include_condition,
                excludeType: self.model.exclude_condition,
              }).get();
              leadsData = self.leads;
  
              // hide loader
              self.hideLoader();
            } catch (error) {
              // show error message
              self.requestErrorAlert(error.response?.data?.error);
  
              return;
            }
          }
  
          // Set various properties on the component based on the fetched data.
          self.leadsFetched = true;
          let rowsThisPage = [];
          rowsThisPage = leadsData.data;
          self.totalPages = leadsData?.last_page;
          self.totalRows = leadsData?.total;
          self.selectedRowSize = leadsData?.per_page;
          const totalNumberOfRows = leadsData.total
            ? leadsData.total
            : leadsData.length;
  
          // Hide or show the row element based on the fetched data.
          const rowEle = document.getElementsByClassName('ag-body-viewport')[0];
          if (leadsData.data && leadsData.data.length === 0) {
            rowEle.setAttribute(
              'style',
              `display: none !important; height: 0px !important; max-height: 0px !important; min-height: 0px !important`
            );
          } else {
            rowEle.setAttribute('style', `display: flex !important`);
          }
  
          // Call the successCallback function with the fetched data.
          setTimeout(() => {
            params.successCallback(rowsThisPage, totalNumberOfRows);
          }, 100);
        };
      }

      // Return a new data source using the MyDatasource constructor.
      return new MyDatasource(Number(this.selectedRowSize));
    },

    showleaddetail(Id) {
      this.$router.push({ name: 'LeadDetail', params: { id: Id } });
    },

    openCreateMaillead() {
      this.$root.$emit('modal-open', 'create-maillead-modal');
      this.showLoader();
      this.fetchLists(null, !(this.lists && this.lists.length));
      this.fetchEventsTags(null, !(this.tags && this.tags.length));
      this.initializeData();
      this.hideLoader();
    },

    closeCreateMaillead() {
      this.initializeData();
      this.$root.$emit('modal-close', 'create-maillead-modal');
    },

    /**
     * Creates a new Maillead object
     * Validates the form data before submitting.
     * @returns {Promise<void>} A Promise that resolves when the Maillead is saved successfully.
     */

    async createMaillead() {
      // Set the submitted flag to true to show validation errors.
      this.submitted = true;

      // Validate the form data using VeeValidate.
      this.$v.model.first_name.$touch();
      this.$v.model.last_name.$touch();
      this.$v.model.email.$touch();
      this.$v.model.leadlists.$touch();
      this.$v.model.phone_number.$touch();

      if (
        !this.$v.model.first_name.$invalid &&
        !this.$v.model.last_name.$invalid &&
        !this.$v.model.email.$invalid &&
        !this.$v.model.leadlists.$invalid &&
        (this.phoneData.isValid || !this.model.phone_number)
      ) {
        // Show the loader while saving the Maillead.
        this.showLoader();

        // Create a new Maillead object and set its properties.
        this.maillead = new Maillead({});
        this.maillead.email = this.model.email.toLowerCase();

        // Set the Maillead's tags based on the selected tags in the form.
        if (this.model.leadtags && this.model.leadtags.length) {
          const choosenTags = [];
          this.model.leadtags.forEach((ele) => {
            const tagFound = this.tags.find((data) => data.id === ele);
            choosenTags.push(tagFound);
          });
          this.maillead.tags = choosenTags || [];
        } else {
          this.maillead.tags = [];
        }

        // Set the Maillead's lists based on the selected lists in the form.
        if (this.model.leadlists && this.model.leadlists.length) {
          const choosenLists = [];
          this.model.leadlists.forEach((ele) => {
            const listFound = this.lists.find((data) => data.id === ele);
            choosenLists.push(listFound);
          });
          this.maillead.lists = choosenLists || [];
        } else {
          this.maillead.lists = [];
        }

        // Set the Maillead's contact object based on the form data.
        this.maillead.contact = {
          email: this.model.email.toLowerCase(),
          first_name: this.model.first_name,
          last_name: this.model.last_name,
          phone_number: this.model.phone_number,
          company: this.model.company,
          address_1: this.model.address_1,
          address_2: this.model.address_2,
          city: this.model.city,
          state: this.model.state,
          country: this.model.country,
          zip: this.model.zip,
          url: this.model.url,
          billing_address: this.model.billing_address,
          tax_number: this.model.tax_number,
          ip_address: this.model.ip_address,
        };

        // Set the Maillead's country code and symbol based on the phone number data.
        if (this.model.phone_number) {
          this.maillead.contact.country_code = `+${this.phoneData.countryCallingCode}`;
          this.maillead.contact.country_symbol = this.phoneData.countryCode;
        }

        // Set the Maillead's is_manually flag to 1.
        this.maillead.is_manually = '1';

        // Save the Maillead to the API and handle the response.
        this.createMailleadToApi();
      }
    },

    // save the Maillead to the API and handle the response.
    async createMailleadToApi() {
      this.leadsFetched = true;
      try {
        // Save the Maillead to the API and handle the response.
        await this.maillead.save();

        // Check if the Maillead has data.
        if(!this.hasData) {
          // made the hasData flag to true
          this.hasData = true;
          
          // change leads fetched flag to false
          this.leadsFetched = false;

          // reset the cache
          this.gridApi.infiniteRowModel.resetCache();
        } else {
          this.fetchEventsList();
        }
        
        // close the create maillead modal
        this.closeCreateMaillead();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    initializeData() {
      this.$v.model.first_name.$reset();
      this.$v.model.last_name.$reset();
      this.$v.model.email.$reset();
      this.$v.model.leadlists.$reset();
      this.$v.model.phone_number.$reset();

      this.submitted = false;
      this.default_country_code = 'GB';
      this.phoneData = {
        countryCallingCode: '44',
        countryCode: 'GB',
        isValid: true,
      };
      this.model.first_name = '';
      this.model.last_name = '';
      this.model.email = '';
      this.model.address_1 = '';
      this.model.phone_number = '';
      this.model.address_2 = '';
      this.model.state = '';
      this.model.country = '';
      this.model.zip = '';
      this.model.billing_address = '';
      this.model.ip_address = '';
      this.model.url = '';
      this.model.tax_number = '';
      this.model.company = '';
      this.model.leadtags = [];
      this.model.leadlists = [];
    },

    async changedSearchOrFilter(val) {
      // show loader
      this.showLoader();

      this.searchPhrase = val;
      this.leadsFetched = false;

      try {
        this.dataSource = await this.getDataSource(Number(this.selectedRowSize));
        this.hideLoader();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async fetchEventsList() {
      // show loader
      this.showLoader();

      try {
        this.leadsFetched = false;
        this.dataSource = await this.getDataSource(Number(this.selectedRowSize));
        this.$refs.agGridTable.resetSelectionData();
        // this.onGridReady();
      } catch (error) {
        // show error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    // error alert message
    requestErrorAlert(errorMessage) {
      // hide loader
      this.hideLoader();

      // show error message
      showAlert({
        iconType: 'alert-error',
        html: `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-lg font-bold text-deep-dark mb-3"> ${this.$t('frontend_groovemail_email_validation_error')}</p>
          <p class="text-sm text-deep-dark"> ${errorMessage || this.$t('frontend_backend_error')}</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    // show the loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide the loader
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    updateLimitBanner(show) {
      this.showLimitBanner = show;
    },

    goToUpgradePage() {
      const user = auth.authenticatedUser();
      const url = window.gdEnv[`UPGRADE_URL_${user?.metadata?.region?.toUpperCase()}`] || window.gdEnv[`UPGRADE_URL`];
      window.open(url, '_blank');
    },

    getLeadsTableColDefs(self) {
      const LeadDetail = 'lead-details-header';

      this.leadsTableColumnDefs = [
        {
          headerName: this.$t('frontend_groovemail_forms_index_name'),
          headerClass: LeadDetail,
          cellClass() {
            return 'leads-name-col';
          },
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.first_name) {
              if (params.data.last_name) {
                return `${params.data.first_name} ${params.data.last_name}`;
              }
              return params.data.first_name;
            }
            if (params.data && params.data.last_name) {
              return params.data.last_name;
            }
            return 'N/A';
          },
          resizable: true,
          width: 200,
          pinned: 'left',
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_email'),
          headerClass: LeadDetail,
          field: 'email',
          valueGetter(params) {
            if(self.demoMode)
              return self.basicEmailText;
            if (params.data && params.data.email) {
              return params.data.email;
            }
            return 'N/A';
          },
          resizable: true,
          width: 200,
        },
        {
          headerName: this.$t('frontend_groovemail_lead_listing_status_col'),
          headerClass: LeadDetail,
          field: 'block_status',
          resizable: true,
          width: 165,
          cellRenderer: (params) =>
            params.value === 'Safe'
              ? `<span class="border border-success px-5 py-1 rounded-md text-success text-xs uppercase">${params.value}</span>`
              : `<span class="border border-danger px-5 py-1 rounded-md text-danger text-xs uppercase">${params.value}</span>`,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_phone'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicContactText;
            if (params.data && params.data.phone_number) {
              if (params.data.country_code) {
                return `${params.data.country_code} ${params.data.phone_number}`;
              }
              return params.data.phone_number;
            }
            return 'N/A';
          },
          resizable: true,
          width: 155,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_company'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.company) {
              return params.data.company;
            }
            return 'N/A';
          },
          resizable: true,
          width: 180,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_adress1'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.address_1) {
              return params.data.address_1;
            }
            return 'N/A';
          },
          resizable: true,
          width: 250,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_adress2'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.address_2) {
              return params.data.address_2;
            }
            return 'N/A';
          },
          resizable: true,
          width: 250,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_country'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.country) {
              return params.data.country;
            }
            return 'N/A';
          },
          resizable: true,
          width: 140,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_state'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.state) {
              return params.data.state;
            }
            return 'N/A';
          },
          resizable: true,
          width: 140,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_city'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.city) {
              return params.data.city;
            }
            return 'N/A';
          },
          resizable: true,
          width: 140,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_zipcode'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.zip) {
              return params.data.zip;
            }
            return 'N/A';
          },
          resizable: true,
          width: 120,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_url'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicUrlText;
            if (params.data && params.data.url) {
              return params.data.url;
            }
            return 'N/A';
          },
          resizable: true,
          width: 180,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_billing'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.billing_address) {
              return params.data.billing_address;
            }
            return 'N/A';
          },
          resizable: true,
          width: 250,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_index_taxnumber'),
          headerClass: LeadDetail,
          valueGetter(params) {
            if(self.demoMode)
              return self.basicText;
            if (params.data && params.data.tax_number) {
              return params.data.tax_number;
            }
            return 'N/A';
          },
          resizable: true,
          width: 130,
        },
        {
          headerName: this.$t('frontend_groovemail_created_at'),
          headerClass: LeadDetail,
          field: 'created_at',
          valueGetter(params) {
            const months = [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dev',
            ];
            if (params.data && params.data.created_at) {
              return `${new Date(params.data.created_at).getDate()} ${
                months[new Date(params.data.created_at).getMonth()]
              }, ${new Date(params.data.created_at).getFullYear()}`;
            }
            return 'N/A';
          },
          resizable: true,
          width: 110,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_show_lists'),
          headerClass: LeadDetail,
          cellRenderer: 'listNamesRenderer',
          field: 'list_count',
          resizable: true,
          width: 70,
        },
        {
          headerName: this.$t('frontend_groovemail_mail_lead_show_tags'),
          headerClass: LeadDetail,
          field: 'tag_count',
          cellRenderer: 'tagNamesRenderer',
          resizable: true,
          width: 70,
        },
        {
          headerName: '',
          headerClass: LeadDetail,
          cellClass: 'actions-button-cell',
          field: 'lead_id',
          cellRenderer: 'actionRenderer',
          resizable: false,
          width: 160,
          pinned: 'right',
        },
      ];
    },
  },

  // check the route before leaving the component
  beforeRouteLeave(to, from, next) {
    // check if the route is ImportedLeadsListing or Suppressions and the filter query is not empty
    if (!(to.name === 'ImportedLeadsListing' || to.name === 'Suppressions') || Object.keys(from.query).length === 0) {
      // clear the filter query from the store when leaving the component to another route
      this.$store.dispatch('clearFilterQuery');
    }

    // complete the route change
    next();
  },

  // check the route before entering the component
  beforeRouteEnter(to, from, next) {
    // Check if the user came from a specific route
    if (from.name === 'ImportedLeadsListing' || from.name === 'Suppressions') {
      // Update the route with the filter query from the store
      next((vm) => {
        // check if the filter query is not empty
        if (Object.keys(vm.filters.filterQuery).length > 0){
          vm.$router.push({ path: to.path, query: vm.filters.filterQuery }).catch(() => {});
        }
      });
    }

    // complete the route change
    next();
  },

  async created() {
    // show loader
    this.showLoader();

    // check if the query is empty
    const isQueryEmpty = Object.keys(this.$route.query).length === 0;

    // check if should apply filters if we came back from show lead and the query is empty
    if(this.filters.isApplyingFilters && isQueryEmpty) {
      // apply filters
      this.model = {
        ...this.model,
        ...this.filters.filterData,
      };
      

      // set the filter empty flag to true
      this.isFilterEmpty = false;

      // apply filter conditions
      await this.applyAdvancedFilters();
    }

    // check if the query is empty
    if (this.totalRows === 0) {
      this.hasData = false;
    } else {
      this.hasData = true;
    }
  },

  async mounted() {
    // show loader
    this.showLoader();

    try {
      // fetch the leads data
      this.contactLimitExceeded = await contactAddLimitExceeded();
      await this.updateLimitBanner(true);
    } catch (error) {
      // show error message
      this.requestErrorAlert(error.response?.data?.error);
    }
  },

  async beforeMount() {
    // show loader
    this.showLoader();

    const self = this;
    this.gridOptions = {};

    this.context = { componentParent: this };
    this.frameworkComponents = {
      actionRenderer: ActionRenderer,
      tagNamesRenderer: TagNamesRenderer,
      listNamesRenderer: ListNamesRenderer,
    };

    this.rowBuffer = 0;
    this.overlayNoRowsTemplate = '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">This is a custom \'no rows\' overlay</span>';
    this.headerHeight = 80;
    this.rowModelType = 'infinite';
    this.paginationPageSize = 10;
    this.cacheOverflowSize = 2;
    this.maxConcurrentDatasourceRequests = 1;
    this.infiniteInitialRowCount = 1;
    this.maxBlocksInCache = 2;
    
    await this.getLeadsTableColDefs(self);
  },

  beforeDestroy() {
    // reset applied filters to false when component is destroyed
    this.$store.dispatch('setApplyingFilter', false);
  },

  computed: {
    // Used to return lists value from store.
    ...mapState(['filters']),

    /**
     * Returns navigation tabs data for leads
     * @return {array} navigation tabs data
    */
    navTabs() {
      return [
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_leads',
          tabRoute: 'Leads2',
          tabQuery: { ...this.$route.query },
        },

        // check if the user can import leads
        this.userCanImportLeads === 1 && this.canImportLeads ? {
          tabName: 'frontend_groovemail_imported_leads_listing_page_imports_nav',
          tabRoute: 'ImportedLeadsListing',
        } : '',

        {
          tabName: 'frontend_groovemail_components_sidebar_suppressions',
          tabRoute: 'Suppressions',
          tabQuery: { type: 'unsubscribes' },
        }
      ];
    },

    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },

    userCanImportLeads() {
      return this.getCanImportLeads();
    },

    bulkActions() {
      const actions = [];
      if (this.isAllSelected || this.selectedLeadIds.length) {
        actions.push({
          id: 1,
          name: this.$t('frontend_groovemail_lists_export_contacts'),
          callback: () => this.downloadLeadExportFile(),
        });
        actions.push({
          id: 2,
          name: this.$t('frontend_groovemail_bulk_delete_enteries'),
          callback: () => this.confirmModalOpen('bulkDelete'),
        });
        actions.push({
          id: 3,
          name: this.$t('frontend_groovemail_bulk_add_lists'),
          callback: () => this.openBulkAddListsModal(),
        });
        actions.push({
          id: 4,
          name: this.$t('frontend_groovemail_bulk_add_tags'),
          callback: () => this.openBulkAddTagsModal(),
        });
      }
      return actions;
    },

    rowSizeFilterData() {
      return [
        {
          text: '10',
          value: 10,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },
        {
          text: '100',
          value: 100,
        },
      ];
    },

    firstIncludeConditionData() {
      switch (this.model.include_mainselect) {
        case 'subscribedlist':
          return this.lists;
        case 'hastag':
          return this.categories;
        case 'hasform':
          return this.forms;
        case 'subscribedsegment':
          return this.segments;
        case 'subscribedsequence':
          return this.sequences;
        case 'hasstatus':
          return this.statuses;
        case 'openbroadcast':
        case 'clickbroadcast':
          return this.campaigns;
        default:
          return null;
      }
    },

    secondIncludeConditionData() {
      switch (this.model.include_mainselect) {
        case 'hastag':
          return this.includefiltertags;
        case 'clickbroadcast':
          return this.includelinks;
        default:
          return null;
      }
    },

    fetchFirstIncludeCondition() {
      switch (this.model.include_mainselect) {
        case 'subscribedlist':
          return this.model.includelist;
        case 'hastag':
          return this.model.includecategory;
        case 'hasform':
          return this.model.includeform;
        case 'subscribedsegment':
          return this.model.includesegment;
        case 'subscribedsequence':
          return this.model.includesequence;
        case 'hasip':
          return this.model.includeip;
        case 'hasstatus':
          return this.model.includestatus;
        case 'indaterange':
          return this.model.includedaterange;
        case 'openbroadcast':
        case 'clickbroadcast':
          return this.model.includecampaign;
        default:
          return '';
      }
    },

    fetchSecondIncludeCondition() {
      switch (this.model.include_mainselect) {
        case 'hastag':
          return this.model.includetag;
        case 'clickbroadcast':
          return this.model.includecampaignlink;
        default:
          return '';
      }
    },

    firstExcludeConditionData() {
      switch (this.model.exclude_mainselect) {
        case 'subscribedlist':
          return this.lists;
        case 'hastag':
          return this.categories;
        case 'hasform':
          return this.forms;
        case 'subscribedsegment':
          return this.segments;
        case 'subscribedsequence':
          return this.sequences;
        case 'hasstatus':
          return this.statuses;
        case 'openbroadcast':
        case 'clickbroadcast':
          return this.campaigns;
        default:
          return null;
      }
    },

    statuses() {
      return [
        {
          id: '0',
          name: this.$t(
            'frontend_groovemail_lead_listing_advanced_filters_status_all'
          ),
        },
        {
          id: 1,
          name: this.$t(
            'frontend_groovemail_lead_listing_advanced_filters_status_subscribed'
          ),
        },
        {
          id: 2,
          name: this.$t(
            'frontend_groovemail_lead_listing_advanced_filters_status_unsubscribed'
          ),
        },
        {
          id: 3,
          name: this.$t(
            'frontend_groovemail_lead_listing_advanced_filters_status_verified'
          ),
        },
        {
          id: 4,
          name: this.$t(
            'frontend_groovemail_lead_listing_advanced_filters_status_unverified'
          ),
        },
        {
          id: 5,
          name: this.$t(
            'frontend_groovemail_lead_listing_advanced_filters_status_blocklisted'
          ),
        },

        {
          id: 6,
          name: this.$t(
            'frontend_groovemail_lead_listing_advanced_filters_status_hard_bounce'
          ),
        },
      ];
    },

    secondExcludeConditionData() {
      switch (this.model.exclude_mainselect) {
        case 'hastag':
          return this.excludefiltertags;
        case 'clickbroadcast':
          return this.excludelinks;
        default:
          return null;
      }
    },

    fetchFirstExcludeCondition() {
      switch (this.model.exclude_mainselect) {
        case 'subscribedlist':
          return this.model.excludelist;
        case 'hastag':
          return this.model.excludecategory;
        case 'hasform':
          return this.model.excludeform;
        case 'subscribedsegment':
          return this.model.excludesegment;
        case 'subscribedsequence':
          return this.model.excludesequence;
        case 'hasip':
          return this.model.excludeip;
        case 'hasstatus':
          return this.model.excludestatus;
        case 'indaterange':
          return this.model.excludedaterange;
        case 'openbroadcast':
        case 'clickbroadcast':
          return this.model.excludecampaign;
        default:
          return '';
      }
    },

    fetchSecondExcludeCondition() {
      switch (this.model.exclude_mainselect) {
        case 'hastag':
          return this.model.excludetag;
        case 'clickbroadcast':
          return this.model.excludecampaignlink;
        default:
          return '';
      }
    },

    binaryConditionData() {
      return [
        {
          value: 'AND',
          name: this.$t('frontend_groovemail_segment_and'),
        },
        {
          value: 'OR',
          name: this.$t('frontend_groovemail_segment_or'),
        },
      ];
    },

    mainConditionData() {
      return [
        {
          value: '',
          dataName: '',
          name: this.$t('frontend_groovemail_segment_select'),
        },
        {
          value: 'subscribedlist',
          dataName: 'Subscribed to list',
          name: this.$t('frontend_groovemail_segment_subscribed_tolist'),
        },
        {
          value: 'hastag',
          dataName: 'Has tag',
          name: this.$t('frontend_groovemail_segment_subscribe_hastag'),
        },
        {
          value: 'hasform',
          dataName: 'Has form',
          name: this.$t('frontend_groovemail_lead_listing_filters_has_form'),
        },
        {
          value: 'subscribedsegment',
          dataName: 'Subscribed to segment',
          name: this.$t(
            'frontend_groovemail_lead_listing_filters_subscribed_to_segment'
          ),
        },
        {
          value: 'subscribedsequence',
          dataName: 'Subscribed to sequence',
          name: this.$t(
            'frontend_groovemail_subscribed_to_sequence_filter_option'
          ),
        },
        {
          value: 'hasip',
          dataName: 'Has Ip',
          name: this.$t('frontend_groovemail_leads_filter_has_ip_option'),
        },
        {
          value: 'openbroadcast',
          dataName: 'Opened a Broadcast',
          name: this.$t(
            'frontend_groovemail_leads_filter_opened_a_broadcast_option'
          ),
        },
        {
          value: 'clickbroadcast',
          dataName: 'Clicked a Link',
          name: this.$t(
            'frontend_groovemail_leads_filter_clicked_a_link_option'
          ),
        },
        {
          value: 'indaterange',
          dataName: 'Date range',
          name: this.$t('frontend_groovemail_leads_filter_date_range_option'),
        },
        {
          value: 'hasstatus',
          dataName: 'Status',
          name: this.$t('frontend_groovemail_leads_filter_status_option'),
        },
      ];
    },

    // add tags data to dropdown list with create new tag option
    tagsData() {
      const tagsData = [];
      if (this.tags && this.tags.length) {
        this.tags.forEach((element) => {
          tagsData.push({ id: element.id, label: element.name });
        });
      }
      const tagIndex = tagsData.findIndex((tagEle) => {
        return tagEle.id === 'createTagOption1';
      });
      if (tagIndex < 0) {
        tagsData.push({
          id: 'createTagOption1',
          label: this.$t('frontend_groovemail_mail_tag_new_tag'),
        });
      }
      return tagsData;
    },


    // add tags data to dropdown for bulk without create new tag option
    tagsDataForBulk() {
      const tagsData = [];
      if (this.tags && this.tags.length) {
        this.tags.forEach((element) => {
          tagsData.push({ id: element.id, label: element.name });
        });
      }

      return tagsData;
    },

    // add list to dropdown list with create new list option
    listData() {
      const listData = [];
      if (this.lists && this.lists.length) {
        this.lists.forEach((element) => {
          listData.push({ id: element.id, label: element.name });
        });
      }

      
      const listIndex = listData.findIndex((listEle) => {
        return listEle.id === 'createListOption1';
      });
      if (listIndex < 0) {
        listData.push({
          id: 'createListOption1',
          label: this.$t('frontend_groovemail_mail_list_newlist'),
        });
      }
      

      return listData;
    },


    // add list to dropdown for bulk without create new list option
    listDataForBulk() {
      const listData = [];
      if (this.lists && this.lists.length) {
        this.lists.forEach((element) => {
          listData.push({ id: element.id, label: element.name });
        });
      }

      return listData;
    },

    admin() {
      return this.$store.state.user.admin;
    },

    // new plans based limit check
    canImportLeads() {
      return auth.fetchFeatureValue('groovemail_can_import_contacts') === 1;
    },

    // new plans based limit check
    canExportLeads() {
      return auth.fetchFeatureValue('groovemail_can_export_leads') === 1;
    },
  },
};
</script>

<style lang="scss">
  .treeselect-select-shadow .vue-treeselect__menu-container .vue-treeselect__menu {
    box-shadow: 0px 0px 40px 0 rgba(22, 29, 37, 0.05) !important;
  }

  .dropdown {
    &-toggle {
      background-image: none !important;
      border: 1px solid dodgerblue !important;
      border-radius: 30px;
      
      &:hover {
        background: transparent;
      }
    }
    
    &-button-container {
      display: flex;
      align-items: center;

      .btnx {
        border-radius: 5px 0px 0px 5px;
      }

      .btn-drop {
        border-radius: 0px 5px 5px 0px;
        border-left: 1px solid rgba(255, 255, 255, 0.2);
      }
    }
  }
  
  // .multiselect__option--selected.multiselect__option--highlight {
  //   background-color: #ff765b !important;
  //   &:after {
  //     background-color: #ff765b !important;
  //   }
  // }

  #advanced-lead-filter
    .bg-white.rounded-lg.shadow-2xl.relative.flex.flex-col.overflow-hidden {
    width: 800px !important;
  }

  .mail-dash-date-picker .vue-daterange-picker .form-control.reportrange-text {
    min-height: 40px;
  }

  .text-deep-dark {
    color: #191d3a;
  }

  .tooltip.info {
    z-index: 110;
  }

  #mail-lead-view {
    button.disabled {
      text-decoration: none;
      pointer-events: none;
      user-select: none;
      -webkit-user-select: none;
      -ms-user-select: none;
      opacity: 0.7;
    }
  
    .leads-name-col:hover {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .btn {
    &-radius {
      border-radius: 6px !important;
    }

    &-width {
      min-width: 88px;
    }
  }

  .modal-filters {
    .w-11\/12 {
      flex: 1;
    }
    .w-1\/12 {
      width: auto;
      button {
        margin-right: 0;
      }
    }
    & > .mt-4 {
      margin: 8px 0;
    }
  }

  .dropdown-opacity-100 span {
    opacity: 1;
  }
</style>

<style scoped>
.gm-leads-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-leads-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

/* ag-grid chrome (third-party; restyled from outside, cell renderers untouched). */
.gm-leads-refresh >>> .gd-ag-grid-table {
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: none;
}

.gm-leads-refresh >>> .ag-root-wrapper {
  border: 0;
  border-radius: var(--gs-radius-md);
}

.gm-leads-refresh >>> .ag-header {
  background: var(--gs-surface-subtle);
  border-bottom: 1px solid var(--gs-border);
}

.gm-leads-refresh >>> .gd-ag-grid-table .ag-header-row {
  border-bottom: 0;
}

.gm-leads-refresh >>> .gd-ag-grid-table .ag-header-cell {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 700;
}

/* !important only because the kit forces a white header hover with !important. */
.gm-leads-refresh >>> .gd-ag-grid-table .ag-header-cell:hover {
  background: var(--gs-surface-subtle) !important;
}

.gm-leads-refresh >>> .gd-ag-grid-table .ag-cell {
  color: var(--gs-foreground);
  font-weight: 500;
}

.gm-leads-refresh >>> .gd-ag-grid-table .ag-row,
.gm-leads-refresh >>> .gd-ag-grid-table .ag-row:nth-child(even) {
  background: var(--gs-surface);
  border-bottom: 1px solid var(--gs-border);
}

.gm-leads-refresh >>> .gd-ag-grid-table .ag-row.ag-row-hover,
.gm-leads-refresh >>> .gd-ag-grid-table .ag-row:hover {
  background: var(--gs-surface-subtle);
}

.gm-leads-refresh >>> .gd-ag-grid-table .ag-row.ag-row-last {
  border-bottom: 0;
}

.gm-leads-refresh >>> .ag-paging-panel {
  color: var(--gs-muted-foreground);
  border-top: 1px solid var(--gs-border);
  font-size: 13px;
}

/* Grid toolbar: title, "Show" label, search. The kit scopes these by id. */
.gm-leads-refresh >>> #gd-ag-grid-table-view .text-2xl.font-bold {
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gm-leads-refresh >>> #gd-ag-grid-table-view .text-2xl.font-bold > span {
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 500;
}

.gm-leads-refresh >>> #gd-ag-grid-table-view label.text-sm {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
}

.gm-leads-refresh >>> #gd-ag-grid-table-view .search-field .h-10 {
  height: 40px;
  color: var(--gs-foreground);
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  padding-left: 38px;
}

.gm-leads-refresh >>> #gd-ag-grid-table-view .search-field .h-10:focus-within {
  border-color: var(--gs-active-border);
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gm-leads-refresh >>> #gd-ag-grid-table-view .search-field .h-10::before {
  content: '';
  display: block;
  position: absolute;
  top: 12px;
  left: 12px;
  width: 16px;
  height: 16px;
  background: var(--gs-muted-foreground);
  background-image: none;
  pointer-events: none;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.3-4.3'/%3E%3C/svg%3E") center / 16px 16px no-repeat;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.3-4.3'/%3E%3C/svg%3E") center / 16px 16px no-repeat;
}

.gm-leads-refresh >>> #gd-ag-grid-table-view .search-field input::placeholder {
  color: var(--gs-muted-foreground);
  opacity: 1;
}

/* "New contact" gd-dropdown is the one primary action on this surface. */
.gm-leads-refresh >>> .dropdown-opacity-100 > .border {
  color: var(--gs-primary-foreground);
  background: var(--gs-primary);
  border-color: var(--gs-primary);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-primary-shadow);
  font-weight: 700;
  transition: transform 150ms ease;
}

.gm-leads-refresh >>> .dropdown-opacity-100 > .border:hover {
  transform: translateY(-1px);
}

.gm-leads-refresh >>> .dropdown-opacity-100 > .border span {
  color: inherit;
}

.gm-leads-refresh >>> .dropdown-opacity-100 > .relative.z-10 {
  margin-top: 4px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-md);
}

@media (max-width: 640px) {
  .gm-leads-refresh {
    padding: 18px 12px 32px;
  }

  .gm-leads-refresh__workspace {
    padding: 16px;
  }
}
</style>
